import type { TenantWithOwner } from "@repo/shared/types";
import { Check, Pencil, Trash2, Users } from "lucide-react";
import { useTransition } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Link } from "@/components/ui/link";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError, del } from "@/lib/api/_client";
import { checkTenantDelete } from "@/lib/api/admin";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./tenants-table.i18n";

type TenantsTableProps = {
  /** The current page of tenants, already filtered and ordered by the server (QL-136). */
  tenants: TenantWithOwner[];
  onEdit: (tenant: TenantWithOwner) => void;
  canDelete?: boolean;
  mutate?: () => void;
  /** QL-136 — what to say when the list is empty; the default assumes no tenants exist at all. */
  emptyMessage?: string;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** True while the first page loads; the empty state waits for it. */
  isLoading?: boolean;
  /** True while another page or filter is loading; the stale rows dim. */
  isFetching?: boolean;
};

export function TenantsTable({
  tenants,
  onEdit,
  canDelete = true,
  mutate,
  emptyMessage,
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  isLoading,
  isFetching,
}: TenantsTableProps) {
  const t = useT(T);

  const [isPending, startTransition] = useTransition();
  const { confirm, ConfirmationDialog } = useConfirm();

  const showDeleteError = (description: string) =>
    confirm({
      title: t.error,
      description,
      confirmText: t.ok,
      variant: "destructive",
      type: "alert",
    });

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: t.deleteTenant,
      description: `${t.deleteConfirmation} "${name}"? ${t.cannotBeUndone}`,
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    startTransition(async () => {
      try {
        await del(`/internal/admin/tenants/${id}`);
        mutate?.();
      } catch (err) {
        await showDeleteError(
          err instanceof ApiError ? err.message || t.failedToDeleteTenant : t.failedToDeleteTenant,
        );
      }
    });
  };

  // QL-168 — a tenant that still has users or owns tenants shows why straight
  // away; the confirmation opens only when the delete would go through.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: checkTenantDelete,
    onAllowed: (id) => {
      const tenant = tenants.find((row) => row.id === id);
      if (tenant) void handleDelete(tenant.id, tenant.name);
    },
    onBlocked: (reason) => void showDeleteError(reason || t.failedToDeleteTenant),
  });

  if (isLoading) {
    return null;
  }

  if (tenants.length === 0) {
    return <EmptyState>{emptyMessage ?? t.noTenantsYet}</EmptyState>;
  }

  return (
    <>
      <Table
        variant="navigation"
        containerClassName={isFetching ? "opacity-50 pointer-events-none transition-opacity" : ""}
      >
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead>{t.slug}</TableHead>
            <TableHead className="text-center">{t.partner}</TableHead>
            <TableHead>{t.owner}</TableHead>
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tenants.map((tenant) => (
            <TableRow key={tenant.id}>
              <TableCellLink className="font-medium" to={`/tenants/${tenant.id}`}>
                {tenant.name}
              </TableCellLink>
              <TableCell>
                <code className="text-xs bg-muted px-2 py-1 rounded">{tenant.slug}</code>
              </TableCell>
              <TableCell className="text-center">
                {tenant.isPartner && <Check className="h-4 w-4 mx-auto text-primary" />}
              </TableCell>
              <TableCell>
                {tenant.ownerTenant ? (
                  <span className="text-sm text-muted-foreground">{tenant.ownerTenant.name}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableActionsCell>
                {/* asChild, so the tooltip goes on Button and the Link is just
                    the slotted element. The hint used to be a native `title` on
                    the Link, where neither the ban on `title` nor the icon-button
                    rule could see it. */}
                <Button variant="ghost" size="icon" tooltip={t.users} asChild>
                  <Link to={`/tenants/${tenant.id}/users`}>
                    <Users className="h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="ghost" size="icon" tooltip={t.edit} onClick={() => onEdit(tenant)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.delete}
                    loading={checkingId === tenant.id}
                    onClick={() => requestDelete(tenant.id)}
                    disabled={isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
      <ConfirmationDialog />
    </>
  );
}
